// Formulaire de déclaration de panne (responsable de production) : type, équipement, gravité, description, photos.
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Grid, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import { AddAPhotoOutlined, CloseOutlined, ReportProblemOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Panel } from '../../components/ui';
import { panneAPI } from '../../services/api';
import { apiError } from '../../utils/format';
import { tokens } from '../../theme';
import { CATEGORIES, GRAVITES, CategorieIcon, versChamp, versServeur } from './common';

const MAX_PHOTO = 10 * 1024 * 1024;

function Choix({ value, onChange, options, label, render }) {
  return (
    <Box role="radiogroup" aria-label={label} sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${Object.keys(options).length}, 1fr)` } }}>
      {Object.entries(options).map(([k, o]) => {
        const on = value === k;
        return (
          <Box key={k} component="button" type="button" role="radio" aria-checked={on} onClick={() => onChange(k)}
            sx={{
              all: 'unset', cursor: 'pointer', boxSizing: 'border-box', p: 1.5, borderRadius: 2, textAlign: 'center',
              border: `2px solid ${on ? tokens.navy : tokens.line}`, bgcolor: on ? '#E3ECF7' : '#fff',
              transition: 'border-color .15s, background-color .15s', '&:hover': { borderColor: tokens.navy },
              '&:focus-visible': { outline: `3px solid ${tokens.amber}`, outlineOffset: 2 },
            }}>
            {render(k, o, on)}
          </Box>
        );
      })}
    </Box>
  );
}

const PanneForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState({ categorie: '', equipement: '', lieu: '', survenue_le: versChamp(), gravite: '', description: '' });
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const input = useRef(null);

  useEffect(() => {
    if (!id) return;
    panneAPI.get(id).then((r) => {
      const p = r.data;
      setF({ categorie: p.categorie, equipement: p.equipement, lieu: p.lieu || '', survenue_le: versChamp(p.survenue_le), gravite: p.gravite, description: p.description });
    }).catch((e) => setError(apiError(e)));
  }, [id]);

  const ajouterPhotos = (files) => {
    const ok = [];
    for (const file of Array.from(files || [])) {
      if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) { setError(`« ${file.name} » n'est pas une photo (JPG, PNG, WEBP)`); continue; }
      if (file.size > MAX_PHOTO) { setError(`« ${file.name} » dépasse 10 Mo`); continue; }
      ok.push({ file, url: URL.createObjectURL(file) });
    }
    setPhotos((p) => [...p, ...ok].slice(0, 8));
  };

  const valide = f.categorie && f.gravite && f.equipement.trim() && f.description.trim() && f.survenue_le;
  const envoyer = async () => {
    setBusy(true); setError('');
    try {
      const body = { ...f, survenue_le: versServeur(f.survenue_le) };
      if (id) {
        await panneAPI.update(id, body);
        navigate(`/pannes/${id}`, { state: { info: 'Déclaration modifiée' } });
        return;
      }
      const r = await panneAPI.create(body);
      const echecs = [];
      for (const ph of photos) { try { await panneAPI.photo(r.data.id, ph.file); } catch { echecs.push(ph.file.name); } }
      navigate(`/pannes/${r.data.id}`, { state: { info: `${r.data.message}. Les personnes concernées ont été prévenues.${echecs.length ? ` Photo(s) non envoyée(s) : ${echecs.join(', ')}` : ''}` } });
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Layout title={id ? 'Modifier la déclaration de panne' : 'Déclarer une panne'} plain>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Panel title="Quel équipement ?" sx={{ mb: 2 }}>
        <Choix label="Type de panne" value={f.categorie} options={CATEGORIES} onChange={(v) => setF({ ...f, categorie: v })}
          render={(k, o, on) => (
            <>
              <CategorieIcon categorie={k} sx={{ fontSize: 30, color: on ? tokens.navy : tokens.steel }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 0.5 }}>{o.label}</Typography>
            </>
          )} />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={5}>
            <TextField fullWidth required label="Équipement concerné" value={f.equipement} onChange={(e) => setF({ ...f, equipement: e.target.value })}
              placeholder={CATEGORIES[f.categorie]?.exemple || 'ex. Bétonnière n°2'} inputProps={{ maxLength: 120 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField fullWidth label="Lieu" value={f.lieu} onChange={(e) => setF({ ...f, lieu: e.target.value })} placeholder="ex. Atelier coulage, chantier Mbour" inputProps={{ maxLength: 120 }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth required type="datetime-local" label="Survenue le" InputLabelProps={{ shrink: true }} value={f.survenue_le}
              inputProps={{ max: versChamp() }} onChange={(e) => setF({ ...f, survenue_le: e.target.value })} />
          </Grid>
        </Grid>
      </Panel>

      <Panel title="Gravité et description" sx={{ mb: 2 }}>
        <Choix label="Gravité" value={f.gravite} options={GRAVITES} onChange={(v) => setF({ ...f, gravite: v })}
          render={(k, o, on) => (
            <>
              <Box sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, bgcolor: o.bg, color: o.fg, fontSize: 12, fontWeight: 700 }}>{o.court}</Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: on ? 600 : 500, mt: 0.5 }}>{o.label}</Typography>
            </>
          )} />
        {f.gravite === 'ARRET' && (
          <Alert severity="error" icon={<ReportProblemOutlined />} sx={{ mt: 1.5 }}>Les personnes notifiées recevront une alerte <b>URGENTE</b>.</Alert>
        )}
        <TextField fullWidth required multiline minRows={3} label="Description de la panne" sx={{ mt: 2 }} value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })} inputProps={{ maxLength: 4000 }}
          placeholder="Que se passe-t-il ? ex. Le moteur ne démarre plus, bruit anormal au niveau du tambour, fuite d'huile…" />
      </Panel>

      {!id && (
        <Panel title="Photos (facultatif)" sx={{ mb: 2 }} action={<Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>8 photos max · 10 Mo chacune</Typography>}>
          <input ref={input} type="file" accept="image/*" multiple hidden data-testid="panne-photos" onChange={(e) => { ajouterPhotos(e.target.files); e.target.value = ''; }} />
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {photos.map((ph, i) => (
              <Box key={ph.url} sx={{ position: 'relative', width: 96, height: 96, borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${tokens.line}` }}>
                <Box component="img" src={ph.url} alt={ph.file.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <Tooltip title="Retirer"><IconButton size="small" aria-label={`Retirer ${ph.file.name}`} onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                  sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,.9)', '&:hover': { bgcolor: '#fff' } }}><CloseOutlined sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              </Box>
            ))}
            {photos.length < 8 && (
              <Box component="button" type="button" onClick={() => input.current?.click()}
                sx={{ all: 'unset', cursor: 'pointer', width: 96, height: 96, borderRadius: 1.5, border: `2px dashed ${tokens.line}`, display: 'grid', placeItems: 'center', textAlign: 'center', color: tokens.steel, '&:hover': { borderColor: tokens.navy, color: tokens.navy } }}>
                <Box><AddAPhotoOutlined /><Typography sx={{ fontSize: 12, mt: 0.25 }}>Ajouter</Typography></Box>
              </Box>
            )}
          </Box>
        </Panel>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button onClick={() => navigate(-1)}>Annuler</Button>
        <Button variant="contained" color={f.gravite === 'ARRET' ? 'error' : 'primary'} disabled={!valide || busy} onClick={envoyer} startIcon={<ReportProblemOutlined />}>
          {id ? 'Enregistrer' : 'Déclarer la panne'}
        </Button>
      </Box>
    </Layout>
  );
};

export default PanneForm;
